import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useBeforeUnload, useBlocker} from 'react-router-dom';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';

export const BEFORE_ACCOUNT_EXIT = 'autotu:before-account-exit';
export function requestAccountExit(exit) {
  const event = new CustomEvent(BEFORE_ACCOUNT_EXIT, {cancelable:true, detail:{exit}});
  if (window.dispatchEvent(event)) exit();
}

export default function useUnsavedChanges({dirty, busy=false, onDiscard}) {
  const allowed = useRef(false), current = useRef({dirty,busy,onDiscard});
  current.current = {dirty,busy,onDiscard};
  const [pending,setPending] = useState(null);
  const unsafe = () => !allowed.current && (current.current.dirty || current.current.busy);
  const blocker = useBlocker(({currentLocation,nextLocation}) => unsafe() &&
    (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));
  useBeforeUnload(useCallback(event => {
    if (unsafe()) {event.preventDefault();event.returnValue='';}
  }, []));
  const requestLeave = useCallback(action => {
    if (unsafe()) setPending(() => action);
    else action();
  }, []);
  useEffect(() => {
    const listener = event => {
      if (!unsafe()) return;
      event.preventDefault();requestLeave(event.detail.exit);
    };
    window.addEventListener(BEFORE_ACCOUNT_EXIT,listener);
    return () => window.removeEventListener(BEFORE_ACCOUNT_EXIT,listener);
  }, [requestLeave]);
  const stay = () => {setPending(null);if(blocker.state==='blocked')blocker.reset();};
  const leave = () => {
    if(current.current.busy)return;
    allowed.current=true;current.current.onDiscard?.();setPending(null);
    if(blocker.state==='blocked')blocker.proceed();else pending?.();
  };
  return {requestLeave, markSaved:()=>{allowed.current=true;}, markDirty:()=>{allowed.current=false;},
    confirmation:<UnsavedChangesDialog open={!!pending||blocker.state==='blocked'} onStay={stay} onLeave={leave}
      stayLabel="Resta nel modulo" busy={busy}
      description={busy?'Salvataggio in corso. Attendi il risultato prima di uscire.':'Hai modifiche non salvate. Puoi restare per salvarle oppure uscire e perderle.'}/>};
}

// For editors with an existing route blocker: voluntary account/site changes
// must also respect unsaved work. Server revocations never dispatch this event.
export function useAccountExitGuard({dirty,busy=false}) {
  const [pending,setPending]=useState(null);
  useEffect(()=>{
    const listener=event=>{if(!dirty&&!busy)return;event.preventDefault();setPending(()=>event.detail.exit);};
    window.addEventListener(BEFORE_ACCOUNT_EXIT,listener);
    return()=>window.removeEventListener(BEFORE_ACCOUNT_EXIT,listener);
  },[dirty,busy]);
  return <UnsavedChangesDialog open={!!pending} busy={busy} onStay={()=>setPending(null)} onLeave={()=>{if(!busy){const leave=pending;setPending(null);leave?.();}}} description={busy?'Attendi il completamento del salvataggio o del caricamento prima di cambiare sede.':'Stai modificando un elemento. Puoi restare nel modulo oppure cambiare sede e perdere le modifiche non salvate.'}/>;
}
